import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import SEO from '../components/SEO';
import AppSidebar from '../components/common/AppSidebar';
import '../styles/layouts/HomePage.scss';

const ClaimAppointmentPage = () => {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [dataMasked, setDataMasked] = useState(false);
  const navigate = useNavigate();

  // Set data-page attribute on body for CSS targeting
  useEffect(() => {
    document.body.setAttribute('data-page', 'home');
    
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, []);

  // Format date with ordinal suffix and time
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    
    // Format the date part (day with ordinal suffix)
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Format the time part (with AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${ordinalSuffix} ${month} ${year} @ ${formattedHours}:${formattedMinutes}${ampm}`;
  };
  
  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Set up timer to show signup modal after 5 seconds
  useEffect(() => {
    let timer;
    if (claimed && appointment && !loading && !dataMasked) {
      timer = setTimeout(() => {
        setShowSignupModal(true);
      }, 5000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [claimed, appointment, loading, dataMasked]);

  useEffect(() => {
    const claimAppointment = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/appointments/claim/${token}`);
        setAppointment(response.data.appointment);
        setClaimed(true);
        toast.success('Appointment claimed successfully! Full client details are now available.');
      } catch (err) {
        console.error('Error claiming appointment:', err);
        setError(err.response?.data?.error || 'Failed to claim appointment');
        toast.error(err.response?.data?.error || 'Failed to claim appointment');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      claimAppointment();
    }
  }, [token]);

  const handleCreateAccount = () => {
    // Store claim token in localStorage to retrieve after signup
    if (token) {
      localStorage.setItem('claimToken', token);
      localStorage.setItem('claimType', 'appointment');
      
      // Store solicitor email if available from appointment
      if (appointment && appointment.lawyer_email) {
        localStorage.setItem('claimEmail', appointment.lawyer_email);
      }
      
      // Store appointment details to show after account creation
      if (appointment) {
        localStorage.setItem('claimAppointmentDetails', JSON.stringify({
          user_name: appointment.user_name,
          user_email: appointment.user_email,
          user_phone: appointment.user_phone,
          service: appointment.service,
          date: appointment.appointment_date,
          time: appointment.start_time,
          notes: appointment.notes,
          created_at: appointment.created_at
        }));
      }
    }
    
    // Redirect to legal professional signup page
    navigate('/signup/legal-professional?claim=true');
  };

  // Utility function to mask sensitive data
  const maskData = (value, type = 'default') => {
    if (!value || !dataMasked) return value;
    
    switch (type) {
      case 'email':
        const [username, domain] = value.split('@');
        if (username && domain) {
          const maskedUsername = username.length > 2 
            ? username.substring(0, 2) + '*'.repeat(Math.max(3, username.length - 2))
            : '*'.repeat(username.length);
          return `${maskedUsername}@${domain}`;
        }
        return value;
      case 'phone':
        if (value.length > 6) {
          return value.substring(0, 3) + '*'.repeat(value.length - 6) + value.substring(value.length - 3);
        }
        return '*'.repeat(value.length);
      case 'name':
        const words = value.split(' ');
        return words.map(word => 
          word.length > 1 ? word.charAt(0) + '*'.repeat(word.length - 1) : word
        ).join(' ');
      case 'message':
        return dataMasked ? '****** Click "Create Account" to view full details ******' : value;
      default:
        return dataMasked ? '*'.repeat(Math.max(5, value.length)) : value;
    }
  };

  const handleLaterClick = () => {
    setDataMasked(true);
    setShowSignupModal(false);
    toast('Data has been masked for privacy. Create an account to access full details.', {
      icon: '🔒',
      style: {
        borderRadius: '10px',
        background: '#333',
        color: '#fff',
      },
    });
  };

  return (
    <>
      <SEO 
        fallbackTitle="Claim Client Appointment - Zahoot"
        fallbackDescription="Claim your client appointment and access full client details to grow your legal practice."
      />
      <div className="home-page">
        {/* Sidebar Navigation */}
        <AppSidebar />
        
        <main className="home-page__main">
          <div className="max-w-4xl w-full px-4 sm:px-6 lg:px-8 mx-auto z-10 relative">
            
            <div className="text-center mb-8 z-10 relative pt-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Client <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B]">Appointment</span> Details
              </h1>
            </div>
            
            <div className="bg-purple-900/50 rounded-xl p-8 shadow-lg">
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
                  <p className="text-white mt-4">Processing your request...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h2 className="text-xl font-bold text-white mt-4">Error</h2>
                  <p className="text-gray-300 mt-2">{error}</p>
                  <p className="text-gray-300 mt-6">
                    This appointment may have already been claimed or the claim token is invalid.
                    <br />Please check your email for a valid claim link.
                  </p>
                </div>
              ) : claimed && appointment ? (
                <div>
                  <div className="flex items-center justify-center mb-6">
                    <div className="bg-green-500/30 rounded-full p-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-white text-center mb-6">
                    Appointment Successfully Claimed
                  </h2>
                  
                  <p className="text-gray-300 text-center mb-8">
                    You now have access to the full client details. We've also sent a confirmation email with these details to your registered email address.
                  </p>
                  
                  <div className="bg-purple-800/50 rounded-lg p-6 mb-8 border border-purple-700/30">
                    {dataMasked && (
                      <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/40 rounded-lg">
                        <div className="flex items-center justify-between text-amber-300">
                          <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="text-sm font-medium">Data Masked for Privacy</span>
                          </div>
                          <button
                            onClick={handleCreateAccount}
                            className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded transition-all"
                          >
                            Create Account
                          </button>
                        </div>
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-white mb-4">Client Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm">Full Name</p>
                        <p className="text-white">{maskData(appointment.user_name, 'name')}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Email Address</p>
                        <p className="text-white">{maskData(appointment.user_email, 'email')}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Phone Number</p>
                        <p className="text-white">{maskData(appointment.user_phone, 'phone')}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Date</p>
                        <p className="text-white">{appointment.appointment_date ? formatDate(appointment.appointment_date) : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Time</p>
                        <p className="text-white">{appointment.start_time || 'N/A'}</p>
                      </div>
                      {appointment.notes && (
                        <div className="md:col-span-2">
                          <p className="text-gray-400 text-sm">Notes</p>
                          <p className="text-white">{maskData(appointment.notes, 'message')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-gray-300 mb-6">
                      We recommend contacting the client as soon as possible to confirm their appointment.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      {dataMasked ? (
                        <button
                          disabled
                          className="btn bg-gray-600 text-gray-300 font-medium py-3 px-6 rounded-full cursor-not-allowed opacity-50"
                        >
                          Create Account to Contact Client
                        </button>
                      ) : (
                        <>
                          <a
                            href={`mailto:${appointment.user_email}?subject=Confirming Your Appointment&body=Dear ${appointment.user_name},%0A%0AThank you for booking an appointment with us for ${formatDate(appointment.appointment_date)}.%0A%0AI'm looking forward to meeting with you.%0A%0ABest regards,`}
                            className="btn bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white font-medium py-3 px-6 rounded-full hover:opacity-90 transition-all"
                          >
                            Reply via Email
                          </a>
                          
                          {appointment.user_phone && (
                            <a
                              href={`tel:${appointment.user_phone}`}
                              className="btn bg-white text-gray-800 font-medium py-3 px-6 rounded-full hover:bg-gray-100 transition-all"
                            >
                              Call Client
                            </a>
                          )}
                        </>
                      )}
                    </div>
                    
                    <Link to="/lawyer-dashboard" className="inline-block mt-8 text-pink-300 hover:text-pink-200">
                      Go to Dashboard →
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-white">No appointment information available.</p>
                </div>
              )}
            </div>
            
            <div className="mt-8 text-center">
              <Link to="/" className="text-gray-300 hover:text-white">
                Return to Home
              </Link>
            </div>
          </div>
        </main>
      </div>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fadeIn border border-purple-700/30 max-h-[90vh] overflow-y-auto my-4">
            <div className="text-center mb-6">
              <div className="mx-auto w-20 h-20 flex items-center justify-center bg-purple-600/30 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Create Your Verified Account</h3>
              <p className="text-gray-300 mb-2">
                To continue managing this booking and access all features, please create your verified solicitor account.
              </p>
              <p className="text-pink-300 text-sm mb-6 font-medium">
                FREE subscription for a limited time only!
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white/10 p-4 rounded-lg border border-white/10">
                <h4 className="font-medium text-white mb-2">Benefits include:</h4>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Instant access to client inquiries & bookings</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Enhanced listing for your practice</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Secure messaging with potential clients</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleLaterClick}
                  className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all border border-white/10"
                >
                  Later
                </button>
                <button
                  onClick={handleCreateAccount}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:opacity-90 text-white font-medium rounded-lg transition-all"
                >
                  Create Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClaimAppointmentPage; 